import { gql } from '@apollo/client';
import {
  HardCodedDerivedFieldMetaFieldsFragment,
  RawSqlDerivedFieldMetaFieldsFragment,
} from '../fields';

const RawSqlDefinitionMetaFieldsFragment = gql`
  fragment RawSqlDefinitionMetaFields on RawSqlDefinitionMeta {
    column
    rawSql
    fullDataRequired
  }
`;

const MappedStringDefinitionMetaFieldsFragment = gql`
  fragment MappedStringDefinitionMetaFields on MappedStringDefinitionMeta {
    column
    mappedColumn
    mapElse
    mapNull
    values {
      original
      mapped
    }
  }
`;

const ColumnDefinitionMetaFieldsFragment = gql`
  fragment ColumnDefinitionMetaFields on ColumnDefinitionMeta {
    ... on RawSqlDefinitionMeta {
      ...RawSqlDefinitionMetaFields
    }
    ... on MappedStringDefinitionMeta {
      ...MappedStringDefinitionMetaFields
    }
  }
  ${RawSqlDefinitionMetaFieldsFragment}
  ${MappedStringDefinitionMetaFieldsFragment}
`;

const StructFieldMetaFieldsFragment = gql`
  fragment StructFieldMetaFields on StructFieldMeta {
    type
    name
  }
`;

const StructDefinitionMetaFieldsFragment = gql`
  fragment StructDefinitionMetaFields on StructDefinitionMeta {
    fields {
      ...StructFieldMetaFields
    }
  }
  ${StructFieldMetaFieldsFragment}
`;

const MapDefinitionMetaFieldsFragment = gql`
  fragment MapDefinitionMetaFields on MapDefinitionMeta {
    keyType
    valueType
  }
`;

const FieldTypeParamsFieldsFragment = gql`
  fragment FieldTypeParamsFields on FieldTypeParams {
    fixedTz
    linearDistribution
    arrayItemType
    customDefinition {
      ...ColumnDefinitionMetaFields
    }
    structDefinition {
      ...StructDefinitionMetaFields
    }
    mapDefinition {
      ...MapDefinitionMetaFields
    }
  }
  ${ColumnDefinitionMetaFieldsFragment}
  ${StructDefinitionMetaFieldsFragment}
  ${MapDefinitionMetaFieldsFragment}
`;

const TableFieldFieldsFragment = gql`
  fragment TableFieldFields on TableField {
    tableKey
    column
    type
    typeParams {
      ...FieldTypeParamsFields
    }
  }
  ${FieldTypeParamsFieldsFragment}
`;

const SchemaFieldMetaFieldsFragment = gql`
  fragment SchemaFieldMetaFields on SchemaFieldMeta {
    column
    name
    type
    key
    filter
    groupby
    deprecated
    description
    tableFields {
      ...TableFieldFields
    }
    fieldClass
    derivedDefinition {
      ... on HardCodedDerivedFieldMeta {
        ...HardCodedDerivedFieldMetaFields
      }
      ... on RawSqlDerivedFieldMeta {
        ...RawSqlDerivedFieldMetaFields
      }
    }
  }
  ${TableFieldFieldsFragment}
  ${HardCodedDerivedFieldMetaFieldsFragment}
  ${RawSqlDerivedFieldMetaFieldsFragment}
`;

const TableJoinMetaFieldsFragment = gql`
  fragment TableJoinMetaFields on TableJoinMeta {
    joinPairs {
      factFieldKey
      dimTableFieldColumn
      relOp
    }
    sqlCondition
    temporalCondition {
      temporalJoinType
      factTemporalKey
      dimTemporalKeys
    }
  }
`;

const CustomJoinMetaFieldsFragment = gql`
  fragment CustomJoinMetaFields on CustomJoinMeta {
    description
  }
`;

const TableMetaFieldsFragment = gql`
  fragment TableMetaFields on TableMeta {
    key
    name
    dateRangePredicateColumn
    partitionDelegates
  }
`;

export const FactFieldsFragment = gql`
  fragment FactFields on FactDataModel {
    key
    tableKeys
    fields {
      ...SchemaFieldMetaFields
    }
  }
  ${SchemaFieldMetaFieldsFragment}
`;

export const DimensionFieldsFragment = gql`
  fragment DimensionFields on DimensionDataModel {
    key
    tableKeys
    fields {
      ...SchemaFieldMetaFields
    }
    join {
      ... on CustomJoinMeta {
        ...CustomJoinMetaFields
      }
      ... on TableJoinMeta {
        ...TableJoinMetaFields
      }
    }
  }
  ${SchemaFieldMetaFieldsFragment}
  ${CustomJoinMetaFieldsFragment}
  ${TableJoinMetaFieldsFragment}
`;

export const TableColumnFieldsFragment = gql`
  fragment TableColumnFields on TableColumn {
    column
    type
    typeParams {
      ...FieldTypeParamsFields
    }
    description
  }
  ${FieldTypeParamsFieldsFragment}
`;

const CdwObjectPathComponentFieldsFragment = gql`
  fragment CdwObjectPathComponentFields on CdwObjectPathComponent {
    name
    displayType
  }
`;

const CdwTableFieldsFragment = gql`
  fragment CdwTableFields on CdwTable {
    shortName
    sqlName
  }
`;

export const CdwObjectsContainerFieldsFragment = gql`
  fragment CdwObjectsContainerFields on CdwObjectsContainer {
    path {
      ...CdwObjectPathComponentFields
    }
    containers {
      ...CdwObjectPathComponentFields
    }
    tables {
      ...CdwTableFields
    }
  }
  ${CdwObjectPathComponentFieldsFragment}
  ${CdwTableFieldsFragment}
`;

const BasicConnectionMetaFieldsFragment = gql`
  fragment BasicConnectionMetaFields on BasicConnectionMeta {
    strategy
    jdbcUrl
    username
    timeout
    authType
  }
`;

const BigQueryConnectionMetaFieldsFragment = gql`
  fragment BigQueryConnectionMetaFields on BigQueryConnectionMeta {
    strategy
    jdbcUrl
    account
    projectId
    computeProjectId
    dataset
    timeout
  }
`;

const CurrentEndDateMetaFieldsFragment = gql`
  fragment CurrentEndDateMetaFields on CurrentEndDateMeta {
    kind
  }
`;
const StartEndDateMetaFieldsFragment = gql`
  fragment StartEndDateMetaFields on StartEndDateMeta {
    kind
    startTimeUnit
  }
`;
const StaticEndDateMetaFieldsFragment = gql`
  fragment StaticEndDateMetaFields on StaticEndDateMeta {
    kind
    staticDate
    staticDateAsLiveStart
  }
`;
const RollingEndDateMetaFieldsFragment = gql`
  fragment RollingEndDateMetaFields on RollingEndDateMeta {
    kind
    rollingCount
    rollingTimeUnit
  }
`;

const DataRefreshConfigMetaFieldsFragment = gql`
  fragment DataRefreshConfigMetaFields on DataRefreshConfigMeta {
    lookBackSeconds
    minimumRefreshSeconds
    pfvStringMaxLength
    pfvMaxNumOfValuesPerKey
  }
`;

const SchemaDsConfigFieldsFragment = gql`
  fragment SchemaDsConfigFields on SchemaDsConfig {
    path {
      ...CdwObjectPathComponentFields
    }
    connection {
      ... on BasicConnectionMeta {
        ...BasicConnectionMetaFields
      }
      ... on BigQueryConnectionMeta {
        ...BigQueryConnectionMetaFields
      }
    }
    endDate {
      ... on CurrentEndDateMeta {
        ...CurrentEndDateMetaFields
      }
      ... on StartEndDateMeta {
        ...StartEndDateMetaFields
      }
      ... on StaticEndDateMeta {
        ...StaticEndDateMetaFields
      }
      ... on RollingEndDateMeta {
        ...RollingEndDateMetaFields
      }
    }
    dateDt
    timezone
    weekStart
  }
  ${CdwObjectPathComponentFieldsFragment}
  ${BasicConnectionMetaFieldsFragment}
  ${BigQueryConnectionMetaFieldsFragment}
  ${CurrentEndDateMetaFieldsFragment}
  ${StartEndDateMetaFieldsFragment}
  ${StaticEndDateMetaFieldsFragment}
  ${RollingEndDateMetaFieldsFragment}
`;

const DataModelTemporalFieldRefFieldsFragment = gql`
  fragment DataModelTemporalFieldRefFields on DataModelTemporalFieldRef {
    fieldKey
    columnRefs {
      tableKey
      column
      isAutoGenerated
    }
  }
`;

const DataModelTemporalFieldRefsFieldsFragment = gql`
  fragment DataModelTemporalFieldRefsFields on DataModelTemporalFieldRefs {
    viewKey
    ts {
      ...DataModelTemporalFieldRefFields
    }
    hourOfDay {
      ...DataModelTemporalFieldRefFields
    }
    date {
      ...DataModelTemporalFieldRefFields
    }
    dayOfWeek {
      ...DataModelTemporalFieldRefFields
    }
    week {
      ...DataModelTemporalFieldRefFields
    }
    fourWeek {
      ...DataModelTemporalFieldRefFields
    }
    month {
      ...DataModelTemporalFieldRefFields
    }
    quarter {
      ...DataModelTemporalFieldRefFields
    }
    year {
      ...DataModelTemporalFieldRefFields
    }
  }
  ${DataModelTemporalFieldRefFieldsFragment}
`;

const FunnelPartitionFieldsFragment = gql`
  fragment FunnelPartitionFields on FunnelPartition {
    fieldKey
    name
    key
  }
`;

export const SchemaDataModelFieldsFragment = gql`
  fragment SchemaDataModelFields on SchemaDataModel {
    key
    name
    disabled
    version
    tables {
      ...TableMetaFields
    }
    eventTemporals {
      ...DataModelTemporalFieldRefsFields
    }
    installTemporals {
      ...DataModelTemporalFieldRefsFields
    }
    fact {
      ...FactFields
    }
    dimensions {
      ...DimensionFields
    }
    derivedFields {
      ...SchemaFieldMetaFields
    }
    lockedTtlSeconds
    dsConfig {
      ...SchemaDsConfigFields
    }
    subjects {
      viewKey
      fieldKey
      subjectKey
      name
    }
    sampling {
      default
      cohort
    }
    funnelPartitions {
      ...FunnelPartitionFields
    }
    dataRefresh {
      ...DataRefreshConfigMetaFields
    }
    description
  }
  ${TableMetaFieldsFragment}
  ${FactFieldsFragment}
  ${DimensionFieldsFragment}
  ${SchemaFieldMetaFieldsFragment}
  ${SchemaDsConfigFieldsFragment}
  ${DataModelTemporalFieldRefsFieldsFragment}
  ${FunnelPartitionFieldsFragment}
  ${DataRefreshConfigMetaFieldsFragment}
`;

export const PreviewColumnDataFieldsFragment = gql`
  fragment PreviewColumnDataFields on PreviewColumnData {
    column
    type
    values
  }
`;
